import React, { useState, useEffect } from 'react';
import cx from 'classnames';

import CenteredModal from './CenteredModal';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';

export interface EditNameModalProps {
  heading: string;
  subHeading?: string;
  placeholder?: string;
  open?: boolean;
  loading?: boolean;
  defaultValue?: string;
  clearOnFocus?: boolean;
  confirmButtonText?: string;
  showCancelButton?: boolean;
  onConfirmed?: (name: string) => Promise<void>;
  onRequestClose: (e?: React.MouseEvent | React.KeyboardEvent) => void;
}

export default function EditNameModal({
  heading,
  subHeading = '',
  placeholder = '',
  open = false,
  loading = false,
  defaultValue = '',
  clearOnFocus = false,
  confirmButtonText = 'Save',
  showCancelButton = false,
  onConfirmed,
  onRequestClose,
}: EditNameModalProps) {
  const [name, setName] = useState<string>('');

  const handleConfirmClick = () => {
    onConfirmed?.(name);
  };

  useEffect(() => {
    if (defaultValue && !name) {
      setName(defaultValue);
    }
  }, [defaultValue, open]);

  const resetTextWhenFocussed = (e: React.FocusEvent<HTMLInputElement>) => {
    if (clearOnFocus) {
      e.target.value = '';
      setName('');
    }
  };

  return (
    <CenteredModal
      open={open}
      onRequestClose={onRequestClose}
      loading={loading}
      heading={heading}
      width={434}
    >
      {subHeading && <p className='v2-text-neutral-900'>{subHeading}</p>}
      <Input
        type='text'
        className={cx('mb-[15px]', { '!border-red': !name })}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onChange={(event: React.FormEvent<HTMLInputElement>) =>
          setName(event.currentTarget.value.trim())
        }
        onFocus={resetTextWhenFocussed}
      />
      <div
        className={cx({
          'flex justify-end gap-x-4': showCancelButton,
        })}
      >
        {showCancelButton && (
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onRequestClose}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button
          variant={ButtonVariant.Primary}
          className={cx({
            'w-full': !showCancelButton,
          })}
          onClick={handleConfirmClick}
          disabled={loading || !name}
        >
          {confirmButtonText} {loading && <Spinner className='ml-2' />}
        </Button>
      </div>
    </CenteredModal>
  );
}
